import moment from "moment";
import { SetStateAction } from "react";
import { Event } from "../models/Event";

export type MonthScheduledValues = {
	ScheduledChargedTons: number | null;
	ScheduledRolledTons: number | null;
	ScheduledFinishedTons: number | null;
	ScheduledRollingHours: number | null;
};

/**
 * Returns true when the event (using OriginalStart/OriginalEnd) spans two calendar months.
 */
export const isMultiMonthEvent = (
	originalStart: Date | undefined,
	originalEnd: Date | undefined,
): boolean => {
	if (!originalStart || !originalEnd) return false;
	return moment(originalStart).month() !== moment(originalEnd).month();
};

/**
 * Returns the fraction of the event duration that falls in the given calendar month.
 * calendarMonth is 0-indexed (same as Date.getMonth()).
 */
export const getEventMonthRatio = (
	originalStart: Date,
	originalEnd: Date,
	calendarMonth: number,
): number => {
	const totalHours = moment(originalEnd).diff(
		moment(originalStart),
		"hours",
		true,
	);
	if (totalHours <= 0) return 1;
	const boundary = moment(originalStart)
		.clone()
		.add(1, "month")
		.startOf("month");
	const hoursInFirst = Math.max(
		0,
		Math.min(totalHours, boundary.diff(moment(originalStart), "hours", true)),
	);
	const ratio1 = hoursInFirst / totalHours;
	return moment(originalStart).month() === calendarMonth ? ratio1 : 1 - ratio1;
};

/**
 * Splits a total of rolling hours between the two calendar months an event spans.
 * The first month can only absorb as many rolling hours as the event physically
 * occupies within it (its remaining hours); the surplus spills into the next month.
 * Returns [firstMonthHours, secondMonthHours].
 */
export const splitRollingHoursByMonth = (
	start: Date,
	end: Date,
	monthBoundary: Date,
	totalRollingHours: number,
): [number, number] => {
	const totalHours = moment(end).diff(moment(start), "hours", true);
	if (totalHours <= 0 || totalRollingHours <= 0) return [totalRollingHours, 0];

	const hoursInFirst = Math.max(
		0,
		Math.min(
			totalHours,
			moment(monthBoundary).diff(moment(start), "hours", true),
		),
	);
	const firstHours = Math.min(totalRollingHours, hoursInFirst);
	return [firstHours, totalRollingHours - firstHours];
};

/**
 * Fraction of an event's scheduled values that belongs to the given calendar month.
 * Rolling hours are capped at each month's remaining hours, and tons follow that same
 * split so tonnage and hours stay consistent.
 */
export const getEventMonthScheduledRatio = (
	originalStart: Date,
	originalEnd: Date,
	calendarMonth: number,
	totalRollingHours: number | null | undefined,
): number => {
	if (!totalRollingHours || totalRollingHours <= 0) {
		return getEventMonthRatio(originalStart, originalEnd, calendarMonth);
	}
	const monthBoundary = moment(originalStart)
		.clone()
		.add(1, "month")
		.startOf("month")
		.toDate();
	const [firstHours] = splitRollingHoursByMonth(
		originalStart,
		originalEnd,
		monthBoundary,
		totalRollingHours,
	);
	const firstRatio = firstHours / totalRollingHours;
	return moment(originalStart).month() === calendarMonth
		? firstRatio
		: 1 - firstRatio;
};

/**
 * Prorates event-level scheduled values between two calendar months.
 * Rolling hours are capped at the first month's remaining hours (the surplus spills
 * into the next month) and the tons follow that rolling-hours split so tonnage and
 * hours stay consistent. Returns [firstMonthValues, secondMonthValues].
 */
export const prorateScheduledByMonth = (
	start: Date,
	end: Date,
	monthBoundary: Date,
	scheduled: {
		ScheduledChargedTons: number | null;
		ScheduledRolledTons: number | null;
		ScheduledFinishedTons: number | null;
		ScheduledRollingHours: number | null;
	},
): [MonthScheduledValues, MonthScheduledValues] => {
	const totalHours = moment(end).diff(moment(start), "hours", true);
	if (totalHours <= 0) {
		const empty: MonthScheduledValues = {
			ScheduledChargedTons: null,
			ScheduledRolledTons: null,
			ScheduledFinishedTons: null,
			ScheduledRollingHours: null,
		};
		return [empty, empty];
	}

	const totalRollingHours = scheduled.ScheduledRollingHours ?? 0;
	const [firstHours, secondHours] = splitRollingHoursByMonth(
		start,
		end,
		monthBoundary,
		totalRollingHours,
	);

	const hoursInFirst = Math.max(
		0,
		Math.min(
			totalHours,
			moment(monthBoundary).diff(moment(start), "hours", true),
		),
	);
	const ratio1 =
		totalRollingHours > 0
			? firstHours / totalRollingHours
			: Math.max(0, Math.min(1, hoursInFirst / totalHours));
	const ratio2 = 1 - ratio1;

	const round2 = (value: number) => Math.round(value * 100) / 100;
	const prorate = (value: number | null, ratio: number): number | null =>
		value != null ? round2(value * ratio) : null;

	return [
		{
			ScheduledChargedTons: prorate(scheduled.ScheduledChargedTons, ratio1),
			ScheduledRolledTons: prorate(scheduled.ScheduledRolledTons, ratio1),
			ScheduledFinishedTons: prorate(scheduled.ScheduledFinishedTons, ratio1),
			ScheduledRollingHours:
				scheduled.ScheduledRollingHours != null ? round2(firstHours) : null,
		},
		{
			ScheduledChargedTons: prorate(scheduled.ScheduledChargedTons, ratio2),
			ScheduledRolledTons: prorate(scheduled.ScheduledRolledTons, ratio2),
			ScheduledFinishedTons: prorate(scheduled.ScheduledFinishedTons, ratio2),
			ScheduledRollingHours:
				scheduled.ScheduledRollingHours != null ? round2(secondHours) : null,
		},
	];
};

export const formatHoursToHHMM = (hours: number): string => {
	const totalMinutes = Math.round(hours * 60);
	const h = Math.floor(totalMinutes / 60);
	const m = totalMinutes % 60;
	return `${h}:${String(m).padStart(2, "0")}`;
};

export const calculateMillYield = (
	chargeTons: number,
	rolledTons: number,
): number => {
	if (rolledTons === 0) return 0;
	return Number(((rolledTons / chargeTons) * 100).toFixed(2));
};

export const calculateFinishedYield = (
	finishedTons: number,
	chargeTons: number,
): number => {
	if (chargeTons === 0) return 0;
	return Number(((finishedTons / chargeTons) * 100).toFixed(2));
};

export const calculateRollingHours = (
	rolledTons: number,
	tonsPerHour: number,
): string => {
	if (tonsPerHour === 0) return "0";
	return ((rolledTons ?? 0) / (tonsPerHour ?? 1)).toFixed(2);
};

export const calculateNewEnd = ({
	startDate,
	estimatedRollingHs,
	setValue,
	setFieldsPrefilled,
}: {
	startDate: Date;
	estimatedRollingHs: number;
	setValue: (key: keyof Event, value: any) => void;
	setFieldsPrefilled: (
		value: SetStateAction<
			Record<string, boolean | "prefilled" | "autocalculated" | "warning">
		>,
	) => void;
}) => {
	const roundingPeriod = 15;

	if (startDate) {
		const durationInHours = estimatedRollingHs;
		const calculatedEndDate = moment(startDate).add(durationInHours, "hours");

		const minutes = calculatedEndDate.minutes();
		const roundedMinutes =
			Math.round(minutes / roundingPeriod) * roundingPeriod;
		const roundedEndDate = calculatedEndDate
			.clone()
			.minutes(roundedMinutes)
			.seconds(0)
			.milliseconds(0);

		setValue("End", roundedEndDate.toDate());

		setFieldsPrefilled((prev) => ({
			...prev,
			end: "autocalculated",
		}));
	}
};
